import { getVirtualPortrait } from '../api/virtualPortrait';
import type { AgiBackTaskInfo } from '../api/agiBackTask';
import type { MediaType } from '../store/seedanceTypes';
import { VIRTUAL_PORTRAIT_PROJECT } from './seedanceConstants';
import {
  pickPortraitMainUrl,
  pickPortraitName,
  pickPortraitPlayableUrl,
  pickPortraitThumbUrl,
  resolvePortraitImageUrl,
  unwrapPortraitListItem,
} from './seedancePortraitUtils';
import { getLocalStorageValue, SEEDANCE_STORAGE_KEYS } from './seedanceStorage';

export interface TaskDetailMaterialRef {
  assetId: string;
  type: MediaType;
  role?: string;
  /** 直链 URL（非 asset://）时直接用于预览 */
  directUrl?: string;
}

export interface TaskDetailMaterialDisplay {
  assetId: string;
  name: string;
  type: MediaType;
  url: string | null;
  thumb: string | null;
  role?: string;
}

const ASSET_SCHEME_RE = /^asset:\/\//i;

function parseAssetSchemeUrl(raw: string): string | null {
  const s = raw.trim();
  if (!ASSET_SCHEME_RE.test(s)) return null;
  const id = s.replace(ASSET_SCHEME_RE, '').trim();
  return id || null;
}

function mediaTypeFromContentItem(item: Record<string, unknown>): MediaType {
  const t = String(item.type ?? '').toLowerCase();
  if (t === 'video_url') return 'video';
  if (t === 'audio_url') return 'audio';
  return 'image';
}

function urlFromContentItem(item: Record<string, unknown>): string {
  const t = String(item.type ?? '').toLowerCase();
  if (t === 'image_url') {
    const nested = item.image_url as { url?: string } | undefined;
    return String(nested?.url ?? '').trim();
  }
  if (t === 'video_url') {
    const nested = item.video_url as { url?: string } | undefined;
    return String(nested?.url ?? '').trim();
  }
  if (t === 'audio_url') {
    const nested = item.audio_url as { url?: string } | undefined;
    return String(nested?.url ?? '').trim();
  }
  return '';
}

/** 从 paramsJson.content（兼容旧 metadata.content）按数组顺序提取（跳过 text）。 */
function extractRefsFromMetadataContent(detail: AgiBackTaskInfo): TaskDetailMaterialRef[] {
  const out: TaskDetailMaterialRef[] = [];
  const seen = new Set<string>();

  const params = detail.paramsJson as Record<string, unknown> | undefined;
  const metadata =
    params?.metadata && typeof params.metadata === 'object'
      ? (params.metadata as Record<string, unknown>)
      : null;
  const content = Array.isArray(params?.content) ? params.content : metadata?.content;
  if (!Array.isArray(content)) return out;

  for (const raw of content) {
    if (!raw || typeof raw !== 'object') continue;
    const item = raw as Record<string, unknown>;
    const typeStr = String(item.type ?? '').toLowerCase();
    if (typeStr === 'text') continue;
    const urlRaw = urlFromContentItem(item);
    if (!urlRaw) continue;
    const assetId = parseAssetSchemeUrl(urlRaw);
    const role = typeof item.role === 'string' ? item.role : undefined;
    const type = mediaTypeFromContentItem(item);
    const key = assetId ? `asset:${assetId}` : `url:${urlRaw}`;
    if (seen.has(key)) continue;
    seen.add(key);
    if (assetId) {
      out.push({ assetId, type, role });
    } else if (/^(https?:|data:|blob:)/i.test(urlRaw)) {
      out.push({
        assetId: urlRaw.slice(0, 48),
        type,
        role,
        directUrl: urlRaw,
      });
    }
  }

  return out;
}

/** 从 detail.materials 按 slotIndex / 数组顺序提取。 */
function extractRefsFromTaskMaterials(detail: AgiBackTaskInfo): TaskDetailMaterialRef[] {
  const materials = detail.materials ?? [];
  if (!materials.length) return [];

  const ordered = [...materials]
    .map((m, index) => ({ m, index }))
    .sort((a, b) => {
      const ai = a.m.slotIndex ?? a.index;
      const bi = b.m.slotIndex ?? b.index;
      return ai - bi;
    });

  const out: TaskDetailMaterialRef[] = [];
  const seen = new Set<string>();

  for (const { m } of ordered) {
    const mat = m.material;
    const meta = mat?.metaJson;
    let assetId = '';
    if (meta && typeof meta === 'object') {
      const mj = meta as Record<string, unknown>;
      assetId = String(
        mj.sourceAssetId ?? mj.assetId ?? mj.virtualPortraitId ?? '',
      ).trim();
    }
    if (!assetId && m.materialId != null) {
      assetId = String(m.materialId);
    }
    if (!assetId) continue;
    const key = `asset:${assetId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const type: MediaType =
      mat?.type === 'video' ? 'video' : mat?.type === 'audio' ? 'audio' : 'image';
    out.push({
      assetId,
      type,
      role: m.slotKind,
    });
  }

  return out;
}

/** 从任务详情中提取需展示的素材引用（严格保持接口顺序）。 */
export function extractTaskDetailMaterialRefs(detail: AgiBackTaskInfo): TaskDetailMaterialRef[] {
  const fromContent = extractRefsFromMetadataContent(detail);
  if (fromContent.length > 0) {
    return fromContent;
  }
  return extractRefsFromTaskMaterials(detail);
}

function placeholderDisplay(ref: TaskDetailMaterialRef): TaskDetailMaterialDisplay {
  return {
    assetId: ref.assetId,
    name: ref.assetId,
    type: ref.type,
    url: null,
    thumb: null,
    role: ref.role,
  };
}

async function fetchMaterialDisplayByAssetId(
  ref: TaskDetailMaterialRef,
  options: {
    groupId: string;
    token: string;
    userId: string;
    onDebug?: (message: string, isError?: boolean) => void;
    signal?: AbortSignal;
  },
): Promise<TaskDetailMaterialDisplay | null> {
  const data = await getVirtualPortrait(
    ref.assetId,
    {
      onDebug: options.onDebug,
      signal: options.signal,
      systemToken: options.token,
      userId: options.userId,
    },
  );
  if (options.signal?.aborted) return null;

  const unwrapped = unwrapPortraitListItem(data);
  const mainRaw =
    ref.type === 'video'
      ? pickPortraitPlayableUrl(unwrapped, 'video')
      : ref.type === 'audio'
        ? pickPortraitPlayableUrl(unwrapped, 'audio')
        : pickPortraitPlayableUrl(unwrapped, 'image');
  const url = resolvePortraitImageUrl(mainRaw);
  if (!url) {
    options.onDebug?.(`任务详情素材：${ref.assetId} 详情无可用 URL`, true);
    return null;
  }
  const thumbRaw = pickPortraitThumbUrl(unwrapped, mainRaw);
  const thumb = resolvePortraitImageUrl(thumbRaw) || url;
  const name = pickPortraitName(unwrapped) || ref.assetId;

  return {
    assetId: ref.assetId,
    name,
    type: ref.type,
    url,
    thumb,
    role: ref.role,
  };
}

export interface ResolveTaskDetailMaterialsOptions {
  onDebug?: (message: string, isError?: boolean) => void;
  signal?: AbortSignal;
}

function directMaterialDisplay(ref: TaskDetailMaterialRef): TaskDetailMaterialDisplay {
  return {
    assetId: ref.assetId,
    name: '参考素材',
    type: ref.type,
    url: ref.directUrl!,
    thumb: ref.directUrl!,
    role: ref.role,
  };
}

/**
 * 按素材 id 调用 `GET /api/virtual_portrait/:id` 解析预览 URL（与工作台一致，不走本地缓存）。
 * 输出顺序与入参 refs 完全一致（并发拉取但不打乱顺序）。
 */
export async function resolveTaskDetailMaterialDisplays(
  refs: TaskDetailMaterialRef[],
  options: ResolveTaskDetailMaterialsOptions = {},
): Promise<TaskDetailMaterialDisplay[]> {
  const { onDebug, signal } = options;
  if (!refs.length) return [];

  const needLookup = refs.filter((r) => !r.directUrl && r.assetId.trim());
  const hasDirectOnly = needLookup.length === 0;
  if (hasDirectOnly) {
    return refs.map((r) => directMaterialDisplay(r));
  }

  const groupId = getLocalStorageValue(SEEDANCE_STORAGE_KEYS.librarySelectedGroupId).trim();
  const token = getLocalStorageValue(SEEDANCE_STORAGE_KEYS.apiKey).trim();
  const userId = getLocalStorageValue(SEEDANCE_STORAGE_KEYS.configUserId).trim();
  const hasAuth = Boolean(userId);

  if (!hasAuth) {
    onDebug?.('任务详情素材：未配置系统令牌与用户 ID，无法按 id 拉取详情', true);
    return refs.map((r) => (r.directUrl ? directMaterialDisplay(r) : placeholderDisplay(r)));
  }
  if (!groupId) {
    onDebug?.('任务详情素材：未填写素材组 ID，无法按 id 拉取详情（请在素材库页保存）', true);
    return refs.map((r) => (r.directUrl ? directMaterialDisplay(r) : placeholderDisplay(r)));
  }

  onDebug?.(`任务详情素材：按素材 ID 拉取详情（${needLookup.length} 个）…`);

  return Promise.all(
    refs.map(async (ref) => {
      if (ref.directUrl) return directMaterialDisplay(ref);
      try {
        const item = await fetchMaterialDisplayByAssetId(ref, {
          groupId,
          token,
          userId,
          onDebug,
          signal,
        });
        return item ?? placeholderDisplay(ref);
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        onDebug?.(`任务详情素材：拉取 ${ref.assetId} 失败 — ${msg}`, true);
        return placeholderDisplay(ref);
      }
    }),
  );
}
